import React from 'react';
import { XMarkIcon, FunnelIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface Props {
  /** 'filter' = JSON custom filter; 'search' = the paste-many-values custom search. */
  kind: 'filter' | 'search';
  /** Banner label (e.g. "Filter: My Saved", "Custom filter", "Custom search"). */
  title: string;
  /** Readable criteria chips (per filter condition, or the search field + values). */
  chips: string[];
  /** Remove the active criteria without opening the modal. */
  onClear: () => void;
  /** Reopen the originating dialog to edit. */
  onEdit?: () => void;
}

/**
 * Inline banner showing what's currently constraining the quotes grid — a custom
 * filter OR a custom search (distinct icon + label so the two aren't conflated) —
 * with a quick "Clear" so users don't have to reopen the dialog.
 */
const QuotesActiveFilterBanner: React.FC<Props> = ({ kind, title, chips, onClear, onEdit }) => {
  const Icon = kind === 'search' ? MagnifyingGlassIcon : FunnelIcon;
  const clearLabel = kind === 'search' ? 'Clear custom search' : 'Clear custom filters';

  return (
    <div className="flex items-center gap-2 flex-wrap rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-[0.75rem] dark:border-indigo-800 dark:bg-indigo-900/20">
      <Icon className="w-4 h-4 text-indigo-600 dark:text-indigo-300 shrink-0" />
      <button
        type="button"
        onClick={onEdit}
        disabled={!onEdit}
        title={onEdit ? 'Edit' : undefined}
        className="font-semibold text-indigo-800 dark:text-indigo-300 hover:underline disabled:no-underline disabled:cursor-default"
      >
        {title}
      </button>

      {chips.length > 0 ? (
        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          {chips.map((c, i) => (
            <span
              key={i}
              className="px-1.5 py-0.5 rounded bg-white border border-indigo-200 text-slate-700 whitespace-nowrap dark:bg-slate-800 dark:border-indigo-800 dark:text-slate-200"
            >
              {c}
            </span>
          ))}
        </div>
      ) : (
        <span className="text-slate-600 dark:text-slate-300">active</span>
      )}

      <button
        type="button"
        onClick={onClear}
        className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded font-medium text-indigo-700 hover:bg-indigo-100 dark:text-indigo-300 dark:hover:bg-indigo-900/40 shrink-0"
      >
        <XMarkIcon className="w-3.5 h-3.5" />
        {clearLabel}
      </button>
    </div>
  );
};

export default QuotesActiveFilterBanner;
